import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Container,
  Card,
  Spinner,
  Alert,
  Button,
  Table,
  Modal,
  Form,
  Row,
  Col,
  InputGroup,
  Badge,
} from 'react-bootstrap';
import { API_BASE } from '../lib/apiBase';

const initialFormState = {
  title: '',
  type: 'Full-time',
  fte: '',
  closingDate: '',
  description: '',
  qualifications: '',
  salaryFrom: '',
  salaryTo: '',
  salaryCurrency: 'AUD',
  salaryPeriod: 'year',
};

const AdminManageJobs = () => {
  const role = sessionStorage.getItem('userRole');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);

  const loadJobs = useCallback(async () => {
    if (!role) {
      setJobs([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API_BASE}/jobs?scope=manage`, {
        headers: { 'x-user-role': role },
        withCredentials: true,
      });
      setJobs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load jobs', err);
      setError('Unable to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

const resetForm = () => {
  setFormData(initialFormState);
  setEditingJob(null);
  setSubmitting(false);
};

const handleEdit = (job) => {
  setEditingJob(job);
  setSuccess('');
  setError('');
  setFormData({
    title: job.title || '',
    type: job.type || 'Full-time',
    fte: job.fte || '',
    closingDate: job.closingDate ? job.closingDate.slice(0, 10) : '',
    description: job.description || '',
    qualifications: Array.isArray(job.qualifications) ? job.qualifications.join('\n') : '',
    salaryFrom: job.salaryFrom !== undefined && job.salaryFrom !== null ? String(job.salaryFrom) : '',
    salaryTo: job.salaryTo !== undefined && job.salaryTo !== null ? String(job.salaryTo) : '',
    salaryCurrency: job.salaryCurrency || 'AUD',
    salaryPeriod: job.salaryPeriod || 'year',
  });
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Job title is required.');
      return false;
    }
    if (!formData.description.trim()) {
      setError('Job description is required.');
      return false;
    }
    if (formData.salaryFrom && Number.isNaN(Number(formData.salaryFrom))) {
      setError('Salary (from) must be a number.');
      return false;
    }
    if (formData.salaryTo && Number.isNaN(Number(formData.salaryTo))) {
      setError('Salary (to) must be a number.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingJob) return;
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      const payload = {
        title: formData.title.trim(),
        type: formData.type,
        fte: formData.fte,
        closingDate: formData.closingDate || undefined,
        description: formData.description,
        qualifications: (formData.qualifications || '')
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter(Boolean),
        salaryFrom: formData.salaryFrom === '' ? undefined : Number(formData.salaryFrom),
        salaryTo: formData.salaryTo === '' ? undefined : Number(formData.salaryTo),
        salaryCurrency: formData.salaryCurrency,
        salaryPeriod: formData.salaryPeriod,
      };

      const res = await axios.patch(
        `${API_BASE}/jobs/${editingJob.id || editingJob._id}`,
        payload,
        {
          headers: { 'x-user-role': role },
          withCredentials: true,
        }
      );

      if (res.status === 202 || res.data?.pending) {
        setSuccess('Changes submitted for admin approval.');
      } else {
        setSuccess('Job updated successfully.');
      }

      resetForm();
      await loadJobs();
    } catch (err) {
      console.error('Failed to update job', err);
      const message = err?.response?.data?.error || 'Unable to update job.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (job) => {
    if (!window.confirm(`Delete "${job.title}"? This cannot be undone.`)) return;
    try {
      setError('');
      setSuccess('');
      const res = await axios.delete(`${API_BASE}/jobs/${job.id || job._id}`, {
        headers: { 'x-user-role': role },
        withCredentials: true,
      });
      if (res.status === 202 || res.data?.pending) {
        setSuccess('Delete request sent for admin approval.');
      } else {
        setSuccess('Job deleted successfully.');
      }
      if (editingJob && (editingJob.id === job.id || editingJob._id === job._id)) {
        resetForm();
      }
      await loadJobs();
    } catch (err) {
      console.error('Failed to delete job', err);
      const message = err?.response?.data?.error || 'Unable to delete job.';
      setError(message);
    }
  };

  const statusBadge = useMemo(
    () => ({
      published: { text: 'Published', variant: 'success' },
      pending: { text: 'Pending Approval', variant: 'warning' },
      rejected: { text: 'Rejected', variant: 'danger' },
    }),
    []
  );

  if (!role) {
    return (
      <Container className="py-5">
        <Alert variant="warning">Please log in as an admin or office user to manage jobs.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Manage Jobs</h2>
          <div className="text-muted">Update or remove positions currently on the site.</div>
        </div>
        <Button variant="outline-secondary" onClick={loadJobs}>
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          <h5 className="mb-3">Current Positions</h5>
          {jobs.length === 0 ? (
            <div className="text-muted">No jobs found.</div>
          ) : (
            <Table responsive bordered hover size="sm" className="align-middle">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Submitted By</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => {
                  const statusInfo = statusBadge[job.status] || { text: job.status, variant: 'secondary' };
                  const pendingDeletion = job.pendingChange?.changeType === 'delete';
                  const pendingUpdate = job.pendingChange?.changeType === 'update';
                  const updated = job.updatedAt ? new Date(job.updatedAt).toLocaleString() : '—';
                  return (
                    <tr key={job.id || job._id}>
                      <td>
                        <div className="fw-semibold">{job.title}</div>
                        <div className="text-muted small">{job.type}{job.fte ? ` • ${job.fte}` : ''}</div>
                      </td>
                      <td>
                        <Badge bg={statusInfo.variant}>{statusInfo.text}</Badge>
                        {pendingUpdate && (
                          <div className="text-muted small">Update awaiting approval</div>
                        )}
                        {pendingDeletion && (
                          <div className="text-danger small">Deletion awaiting approval</div>
                        )}
                      </td>
                      <td>{job.submittedBy || '—'}</td>
                      <td>{updated}</td>
                      <td className="d-flex gap-2">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => handleEdit(job)}
                          disabled={pendingDeletion}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(job)}
                          disabled={pendingDeletion}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={Boolean(editingJob)} onHide={() => { resetForm(); }} size="lg" centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Job</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Job Title *</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Employment Type</Form.Label>
                  <Form.Select name="type" value={formData.type} onChange={handleChange}>
                    <option>Full-time</option>
                    <option>Part-time</option>
                    <option>Casual</option>
                    <option>Contract</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mt-3">
              <Form.Label>FTE / Hours</Form.Label>
              <Form.Control
                type="text"
                name="fte"
                value={formData.fte}
                onChange={handleChange}
                placeholder="e.g., 1.0 (full-time)"
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>Closing Date</Form.Label>
              <Form.Control
                type="date"
                name="closingDate"
                value={formData.closingDate}
                onChange={handleChange}
              />
            </Form.Group>

            <Row className="g-3 mt-1">
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Salary Range</Form.Label>
                  <InputGroup>
                    <Form.Select
                      name="salaryCurrency"
                      value={formData.salaryCurrency}
                      onChange={handleChange}
                      style={{ maxWidth: 110 }}
                    >
                      <option value="AUD">AUD</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </Form.Select>
                    <Form.Control
                      type="text"
                      name="salaryFrom"
                      value={formData.salaryFrom}
                      onChange={handleChange}
                      placeholder="From"
                    />
                    <Form.Control
                      type="text"
                      name="salaryTo"
                      value={formData.salaryTo}
                      onChange={handleChange}
                      placeholder="To"
                    />
                    <Form.Select
                      name="salaryPeriod"
                      value={formData.salaryPeriod}
                      onChange={handleChange}
                      style={{ maxWidth: 120 }}
                    >
                      <option value="year">/year</option>
                      <option value="hour">/hour</option>
                    </Form.Select>
                  </InputGroup>
                  <Form.Text className="text-muted">Optional. Leave blank if not disclosed.</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mt-3">
              <Form.Label>Description *</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>Qualifications & Experience</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="qualifications"
                value={formData.qualifications}
                onChange={handleChange}
                placeholder={"One per line"}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={resetForm} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save Changes'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default AdminManageJobs;
